import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Users, User } from "lucide-react";
import { api, fmtEur, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import CountryCombobox from "../../components/CountryCombobox";
import { toast } from "sonner";

const STATUSES = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "cancelled", label: "Cancelled" },
];

export default function Orders() {
  const [tab, setTab] = useState("");
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const reload = () => {
    setLoading(true);
    const url = tab ? `/admin/orders?status=${tab}` : "/admin/orders";
    api.get(url).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(reload, [tab]);

  const filteredItems = unpaidOnly
    ? items.filter((o) => ["confirmed", "packed", "shipped"].includes(o.status) && o.payment_status !== "paid")
    : items;

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle="Confirm, pack, ship or cancel orders."
        testid="page-admin-orders"
        actions={<NewOrderDialog onCreated={(o) => {
          const msg = o.invoice_number
            ? `Order ${o.order_number} confirmed · ${o.invoice_number}`
            : `Order ${o.order_number} created`;
          toast.success(msg);
          navigate(`/admin/orders/${o.id}`);
        }} />}
      />
      <div className="p-4 sm:p-6">
        <Tabs value={tab || "__all__"} onValueChange={(v) => setTab(v === "__all__" ? "" : v)}>
          <TabsList className="bg-white border border-[#E2E4E0] rounded-md p-0.5 h-auto flex-wrap w-full sm:w-auto" data-testid="admin-orders-tabs">
            {STATUSES.map((s) => (
              <TabsTrigger key={s.key || "__all__"} value={s.key || "__all__"} className="data-[state=active]:bg-[#E9F5EC] data-[state=active]:text-[#1B4332] capitalize px-3 h-8 text-sm" data-testid={`admin-orders-tab-${s.key || "all"}`}>{s.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="mt-2 flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1D1A] cursor-pointer">
            <input
              type="checkbox"
              checked={unpaidOnly}
              onChange={(e) => setUnpaidOnly(e.target.checked)}
              data-testid="admin-orders-unpaid-filter"
            />
            Unpaid confirmed only
          </label>
          {unpaidOnly && (
            <span className="text-xs text-[#5C665A]" data-testid="admin-orders-unpaid-count">
              {filteredItems.length} unpaid
            </span>
          )}
        </div>

        <div className="mt-4 bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          {/* Mobile: card list */}
          <div className="md:hidden divide-y divide-[#E2E4E0]" data-testid="admin-orders-card-list">
            {loading && <div className="px-3 py-8 text-center text-[#5C665A]" data-testid="admin-orders-loading-m">Loading…</div>}
            {!loading && filteredItems.length === 0 && <div className="px-3 py-8 text-center text-[#5C665A]" data-testid="admin-orders-empty-m">No orders in {tab || "all"}.</div>}
            {!loading && filteredItems.map((o) => (
              <Link
                key={o.id} to={`/admin/orders/${o.id}`}
                data-testid={`admin-orders-card-${o.id}`}
                className="block px-3 py-3 hover:bg-[#F9F9F8] active:bg-[#E9F5EC] transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="db-mono text-sm font-medium truncate flex items-center gap-1">
                      {o.has_shortfall && (
                        <span title="Some lines exceed current stock" data-testid={`admin-orders-shortfall-${o.id}`} className="text-[#C94A4A]">⚠</span>
                      )}
                      {o.order_number}
                      {o.is_guest && <Badge className="ml-1 bg-neutral-100 text-neutral-600 border-transparent text-[10px] px-1">Guest</Badge>}
                    </div>
                    <div className="text-sm mt-0.5 truncate">{o.buyer_display}</div>
                    <div className="text-xs text-[#5C665A] truncate">{o.buyer_company || "—"} · {new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <OrderStatusBadge status={o.status} />
                    {["confirmed", "packed", "shipped"].includes(o.status) && (
                      <Badge
                        className={`ml-1 border-transparent text-[10px] px-1 ${o.payment_status === "paid" ? "bg-[#E9F5EC] text-[#1B4332]" : "bg-[#FBF3E8] text-[#8A5A2B]"}`}
                        data-testid={`admin-orders-paid-chip-m-${o.id}`}
                      >
                        {o.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </Badge>
                    )}
                    <div className="db-mono font-semibold mt-1">{fmtEur(o.total || o.subtotal)}</div>
                    <div className="text-xs text-[#5C665A]">{o.lines.length} line{o.lines.length === 1 ? "" : "s"}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8] text-[#5C665A]">
                <tr>
                  <th className="text-left px-3 py-2 db-h font-semibold">Order</th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Buyer</th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Date</th>
                  <th className="text-center px-3 py-2 db-h font-semibold">Status</th>
                  <th className="text-right px-3 py-2 db-h font-semibold">Lines</th>
                  <th className="text-right px-3 py-2 db-h font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#5C665A]" data-testid="admin-orders-loading">Loading…</td></tr>}
                {!loading && filteredItems.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#5C665A]" data-testid="admin-orders-empty">No orders in {tab || "all"}.</td></tr>}
                {filteredItems.map((o) => (
                  <tr key={o.id} className="border-t border-[#E2E4E0] db-row cursor-pointer hover:bg-[#F9F9F8]" onClick={() => navigate(`/admin/orders/${o.id}`)} data-testid={`admin-orders-row-${o.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/admin/orders/${o.id}`} className="db-mono font-medium hover:underline inline-flex items-center gap-1" data-testid={`admin-orders-number-${o.id}`}>
                        {o.has_shortfall && <span className="text-[#C94A4A]" title="Some lines exceed current stock" data-testid={`admin-orders-shortfall-t-${o.id}`}>⚠</span>}
                        {o.order_number}
                      </Link>
                      {o.invoice_number && <div className="text-[10px] text-[#5C665A] db-mono">{o.invoice_number}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {o.is_guest && <User size={12} className="text-[#5C665A]" />}
                        <span>{o.buyer_display}</span>
                        {o.is_guest && <Badge className="bg-neutral-100 text-neutral-600 border-transparent text-[10px] px-1">Guest</Badge>}
                      </div>
                      <div className="text-xs text-[#5C665A]">{o.buyer_company || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-[#5C665A] whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-center">
                      <OrderStatusBadge status={o.status} />
                      {["confirmed", "packed", "shipped"].includes(o.status) && (
                        <Badge
                          className={`ml-1 border-transparent text-[10px] px-1 ${o.payment_status === "paid" ? "bg-[#E9F5EC] text-[#1B4332]" : "bg-[#FBF3E8] text-[#8A5A2B]"}`}
                          data-testid={`admin-orders-paid-chip-${o.id}`}
                        >
                          {o.payment_status === "paid" ? "Paid" : "Unpaid"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right db-mono">{o.lines.length}</td>
                    <td className="px-3 py-2 text-right db-mono font-semibold whitespace-nowrap">{fmtEur(o.total || o.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export function OrderStatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-50", text: "text-amber-800", label: "Pending" },
    confirmed: { bg: "bg-[#E9F5EC]", text: "text-[#1B4332]", label: "Confirmed" },
    packed: { bg: "bg-blue-50", text: "text-blue-800", label: "Packed" },
    shipped: { bg: "bg-[#1A1D1A]", text: "text-white", label: "Shipped" },
    cancelled: { bg: "bg-neutral-100", text: "text-neutral-500", label: "Cancelled" },
  };
  const s = map[status] || map.pending;
  return <Badge className={`${s.bg} ${s.text} border-transparent whitespace-nowrap`}>{s.label}</Badge>;
}

/* -------------------------------------------------- NEW ORDER DIALOG (manual/quick orders) */

function NewOrderDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("buyer"); // buyer | guest
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  // Buyer selection
  const [buyerId, setBuyerId] = useState("");
  // Guest fields
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestCountry, setGuestCountry] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestStreet, setGuestStreet] = useState("");
  const [guestAddress2, setGuestAddress2] = useState("");
  const [guestCity, setGuestCity] = useState("");
  const [guestPostal, setGuestPostal] = useState("");

  // Common
  const [lines, setLines] = useState([]);
  const [discountType, setDiscountType] = useState("__none__");
  const [discountValue, setDiscountValue] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [phyto, setPhyto] = useState(0);
  const [adminNote, setAdminNote] = useState("");
  const [buyerNote, setBuyerNote] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/buyers?status=approved").then((r) => setBuyers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, [open]);

  const reset = () => {
    setMode("buyer"); setBuyerId("");
    setGuestName(""); setGuestEmail(""); setGuestCountry("");
    setGuestPhone(""); setGuestStreet(""); setGuestAddress2(""); setGuestCity(""); setGuestPostal("");
    setLines([]); setDiscountType("__none__"); setDiscountValue(0);
    setShipping(0); setPhyto(0); setAdminNote(""); setBuyerNote("");
  };

  const addLine = () => setLines((prev) => [...prev, { productId: "", sizeId: "", quantity: 1, override: "" }]);
  const patchLine = (i, patch) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const subtotalPreview = useMemo(() => {
    // rough client-side preview; server is authoritative
    let s = 0;
    for (const l of lines) {
      if (!l.sizeId) continue;
      const p = products.find((pp) => pp.id === l.productId);
      const v = p?.size_variants?.find((vv) => vv.id === l.sizeId);
      const unit = l.override !== "" ? Number(l.override) : Number(v?.base_price || 0);
      s += unit * (Number(l.quantity) || 0);
    }
    return s;
  }, [lines, products]);

  const submit = async (confirmNow) => {
    // Basic validation
    if (mode === "buyer" && !buyerId) { toast.error("Choose an existing buyer"); return; }
    if (mode === "guest" && !guestName.trim()) { toast.error("Guest name is required"); return; }
    const validLines = lines.filter((l) => l.sizeId && Number(l.quantity) > 0);
    if (validLines.length === 0) { toast.error("Add at least one line with a size and quantity"); return; }

    setBusy(true);
    try {
      const body = {
        buyer_id: mode === "buyer" ? buyerId : null,
        guest_name: mode === "guest" ? guestName.trim() : null,
        guest_email: mode === "guest" ? (guestEmail.trim() || null) : null,
        guest_country_code: mode === "guest" ? (guestCountry || null) : null,
        guest_phone: mode === "guest" ? (guestPhone.trim() || null) : null,
        guest_street_address: mode === "guest" ? (guestStreet.trim() || null) : null,
        guest_address_line2: mode === "guest" ? (guestAddress2.trim() || null) : null,
        guest_city: mode === "guest" ? (guestCity.trim() || null) : null,
        guest_postal_code: mode === "guest" ? (guestPostal.trim() || null) : null,
        lines: validLines.map((l) => ({
          size_variant_id: l.sizeId,
          quantity: Number(l.quantity),
          unit_price_override: l.override === "" ? null : Number(l.override),
        })),
        discount_type: discountType === "__none__" ? null : discountType,
        discount_value: Number(discountValue) || 0,
        shipping_cost: Number(shipping) || 0,
        phyto_cost: Number(phyto) || 0,
        admin_note: adminNote || null,
        buyer_note: buyerNote || null,
        confirm: !!confirmNow,
      };
      const { data } = await api.post("/admin/orders", body);
      setOpen(false); reset();
      onCreated(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.order_id) {
        toast.error(`Order created pending; confirm failed: ${typeof d.confirm_error === "string" ? d.confirm_error : "check stock"}`);
        setOpen(false); reset();
        onCreated({ id: d.order_id, order_number: "…" });
      } else {
        toast.error(getErrorMessage(err, "Failed"));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button
        data-testid="admin-new-order-open"
        onClick={() => setOpen(true)}
        className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
      >
        <Plus size={14} /> New order
      </Button>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="admin-new-order-dialog">
        <DialogHeader>
          <DialogTitle>New order</DialogTitle>
          <DialogDescription>
            Create a manual order for an existing buyer or a walk-in / guest. No online payment.
          </DialogDescription>
        </DialogHeader>

        {/* Buyer / Guest tabs */}
        <div className="flex gap-1 border-b border-[#E2E4E0]">
          {[["buyer", "Existing buyer", Users], ["guest", "Guest / walk-in", User]].map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              className={`px-3 h-9 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px ${mode === k ? "border-[#2D6A4F] text-[#1B4332] font-medium" : "border-transparent text-[#5C665A] hover:text-[#1A1D1A]"}`}
              onClick={() => setMode(k)}
              data-testid={`admin-new-order-tab-${k}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {mode === "guest" ? (
          <div className="pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-[#5C665A]">Guest name *</label>
              <Input data-testid="admin-new-order-guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} className="h-9" placeholder="Walk-in customer name" />
            </div>
            <div>
              <label className="text-xs text-[#5C665A]">Email (optional)</label>
              <Input data-testid="admin-new-order-guest-email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} className="h-9" type="email" />
            </div>
            <div>
              <label className="text-xs text-[#5C665A]">Phone (optional)</label>
              <Input data-testid="admin-new-order-guest-phone" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} className="h-9" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#5C665A]">Country (optional — drives country pricing)</label>
              <CountryCombobox testid="admin-new-order-guest-country" value={guestCountry} onChange={setGuestCountry} placeholder="No country adjustment" />
              <p className="text-[11px] text-[#5C665A] mt-1">Guests get country pricing if you set a country; personal buyer discounts do not apply.</p>
            </div>
            <div className="sm:col-span-3">
              <label className="text-xs text-[#5C665A]">Street address (optional — used on Pro Forma)</label>
              <AddressAutocomplete
                testid="admin-new-order-guest-street"
                value={guestStreet}
                country={guestCountry}
                onChange={setGuestStreet}
                onPicked={(a) => {
                  if (a.street) setGuestStreet(a.street);
                  if (a.city) setGuestCity(a.city);
                  if (a.postal_code) setGuestPostal(a.postal_code);
                  if (a.country_code) setGuestCountry(a.country_code);
                }}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="text-xs text-[#5C665A]">Address line 2 (optional)</label>
              <Input data-testid="admin-new-order-guest-address2" value={guestAddress2} onChange={(e) => setGuestAddress2(e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="text-xs text-[#5C665A]">Postal code</label>
              <Input data-testid="admin-new-order-guest-postal" value={guestPostal} onChange={(e) => setGuestPostal(e.target.value)} className="h-9" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#5C665A]">City</label>
              <Input data-testid="admin-new-order-guest-city" value={guestCity} onChange={(e) => setGuestCity(e.target.value)} className="h-9" />
            </div>
          </div>
        ) : (
          <div className="pt-3">
            <label className="text-xs text-[#5C665A]">Buyer (approved only)</label>
            <Select value={buyerId} onValueChange={setBuyerId}>
              <SelectTrigger className="h-9" data-testid="admin-new-order-buyer"><SelectValue placeholder="Choose buyer…" /></SelectTrigger>
              <SelectContent>
                {buyers.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.first_name} {b.last_name} · {b.company || "—"} · {b.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {buyerId && (() => {
              const b = buyers.find((x) => x.id === buyerId);
              if (!b) return null;
              return (
                <div className="mt-2 text-xs text-[#5C665A]">
                  Discount: <span className="db-mono">{b.personal_discount_percent || 0}%</span> · Country ID: <span className="db-mono">{b.country_id || "—"}</span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Lines */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="db-h font-semibold text-sm">Lines</div>
            <Button variant="outline" onClick={addLine} className="h-8 text-xs" data-testid="admin-new-order-add-line">
              <Plus size={12} /> Add line
            </Button>
          </div>
          {lines.length === 0 && (
            <div className="text-xs text-[#5C665A] px-2 py-3 bg-[#F9F9F8] rounded border border-dashed border-[#E2E4E0]" data-testid="admin-new-order-lines-empty">
              Add at least one line.
            </div>
          )}
          <div className="space-y-2">
            {lines.map((l, i) => {
              const p = products.find((x) => x.id === l.productId);
              const sizes = p?.size_variants || [];
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`admin-new-order-line-${i}`}>
                  <div className="col-span-12 sm:col-span-4">
                    <label className="text-[10px] text-[#5C665A] uppercase">Product</label>
                    <Select value={l.productId} onValueChange={(v) => patchLine(i, { productId: v, sizeId: "" })}>
                      <SelectTrigger className="h-9" data-testid={`admin-new-order-line-${i}-product`}><SelectValue placeholder="Product" /></SelectTrigger>
                      <SelectContent>
                        {products.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label className="text-[10px] text-[#5C665A] uppercase">Size · SKU · stock</label>
                    <Select value={l.sizeId} onValueChange={(v) => patchLine(i, { sizeId: v })} disabled={!p}>
                      <SelectTrigger className="h-9" data-testid={`admin-new-order-line-${i}-size`}><SelectValue placeholder={p ? "Size" : "Pick product"} /></SelectTrigger>
                      <SelectContent>
                        {sizes.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.sku} · {s.stock_qty}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <label className="text-[10px] text-[#5C665A] uppercase">Qty</label>
                    <Input type="number" min={1} value={l.quantity} onChange={(e) => patchLine(i, { quantity: e.target.value })} className="h-9 text-center db-mono" data-testid={`admin-new-order-line-${i}-qty`} />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <label className="text-[10px] text-[#5C665A] uppercase">Unit € (opt)</label>
                    <Input type="number" step="0.01" value={l.override} onChange={(e) => patchLine(i, { override: e.target.value })} placeholder="auto" className="h-9 text-right db-mono" data-testid={`admin-new-order-line-${i}-price`} />
                  </div>
                  <button className="col-span-12 sm:col-span-1 h-9 text-[#5C665A] hover:text-[#C94A4A] flex items-center justify-center" onClick={() => removeLine(i)} data-testid={`admin-new-order-line-${i}-remove`} type="button">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-[#5C665A]">Discount</label>
            <Select value={discountType} onValueChange={setDiscountType}>
              <SelectTrigger className="h-9" data-testid="admin-new-order-discount-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                <SelectItem value="percent">Percent</SelectItem>
                <SelectItem value="fixed">Fixed €</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Value</label>
            <Input type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="h-9 text-right db-mono" disabled={discountType === "__none__"} data-testid="admin-new-order-discount-value" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Shipping</label>
            <Input type="number" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} className="h-9 text-right db-mono" data-testid="admin-new-order-shipping" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Phyto</label>
            <Input type="number" step="0.01" value={phyto} onChange={(e) => setPhyto(e.target.value)} className="h-9 text-right db-mono" data-testid="admin-new-order-phyto" />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#5C665A]">Admin note (internal)</label>
            <Textarea rows={2} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} data-testid="admin-new-order-admin-note" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Note (visible to buyer / on invoice)</label>
            <Textarea rows={2} value={buyerNote} onChange={(e) => setBuyerNote(e.target.value)} data-testid="admin-new-order-buyer-note" />
          </div>
        </div>

        <div className="text-sm text-right db-mono" data-testid="admin-new-order-preview">
          Preview (before discount/shipping/phyto): <b>{fmtEur(subtotalPreview)}</b>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="admin-new-order-cancel">Cancel</Button>
          <Button disabled={busy} onClick={() => submit(false)} variant="outline" data-testid="admin-new-order-save-pending">
            Save as pending
          </Button>
          <Button disabled={busy} onClick={() => submit(true)} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white" data-testid="admin-new-order-confirm">
            {busy ? "Working…" : "Create & confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
